import React, { useEffect } from 'react'
import {
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Divider
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Modal as EOSModal } from '../_EOS/Modal'
import { PickingValidateBatch } from '../../interfaces/Picking/Picking'
import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { ApiResponse } from '../../typings/ApiResponse'
import { Status } from '../../hooks/useFetchState'
import { Loading } from '../UI'
import { DateTime } from 'luxon'
import {
  getBatchesFromPickingRouteDetail,
  PickingValidationSkuDetailBatchInfoDto
} from './services/batchesService'
import { GenericTableV2, createColumns } from '../_EOS/GenericTable'

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: 400,
    marginTop: 16
  },
  noDataMessage: {
    textAlign: 'center',
    padding: 32,
    color: '#666'
  }
}))

interface BatchesModalProps {
  open: boolean
  onClose: () => void
  batches: PickingValidateBatch[]
  skuName: string
  pickingRouteDetailId: number
}

export const BatchesModal: React.FC<BatchesModalProps> = ({
  open,
  onClose,
  batches,
  skuName,
  pickingRouteDetailId
}) => {
  const classes = useStyles()
  const { state, callApi } =
    useApi<ApiResponse<PickingValidationSkuDetailBatchInfoDto>>()
  const pagination = usePagination(20)

  const formatDate = (dateString: string) => {
    try {
      return DateTime.fromISO(dateString).toFormat('dd/MM/yyyy')
    } catch {
      return 'Fecha inválida'
    }
  }

  const loadBatches = () => {
    if (pickingRouteDetailId && open) {
      callApi(
        getBatchesFromPickingRouteDetail(
          pickingRouteDetailId,
          pagination.page,
          pagination.pageSize
        )
      )
    }
  }

  useEffect(() => {
    loadBatches()
  }, [open, pickingRouteDetailId, pagination.page, pagination.pageSize])

  const handlePageChange = (newPage: number, newPageSize: number) => {
    const auxPage = pagination.pageSize !== newPageSize ? 0 : newPage
    pagination.setPagination(auxPage, newPageSize)
  }

  // Configuración de columnas para GenericTableV2
  const columns = createColumns<PickingValidationSkuDetailBatchInfoDto>([
    {
      key: 'batchNumber',
      title: 'Número de Lote',
      width: 200
    },
    {
      key: 'expirationDate',
      title: 'Fecha de Vencimiento',
      width: 200,
      render: (value, _row) => (value ? formatDate(String(value)) : 'Sin fecha')
    },
    {
      key: 'pickedQty',
      title: 'Cantidad Pickeada',
      width: 150,
      align: 'center'
    }
  ])

  return (
    <EOSModal open={open} onClose={onClose} maxWidth='lg'>
      <DialogTitle>
        <Typography variant='h6' component='div'>
          Información Completa de Lotes
        </Typography>
        <Typography variant='subtitle2' color='textSecondary'>
          {skuName}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {state.status === Status.Loading && <Loading />}

        {state.status === Status.Error && (
          <Box className={classes.noDataMessage}>
            <Typography color='error'>
              Error al cargar los lotes: {state.error}
            </Typography>
          </Box>
        )}

        {state.status === Status.Success && state.data && (
          <Box mt={2}>
            {state.data.items && state.data.items.length > 0 ? (
              <GenericTableV2<PickingValidationSkuDetailBatchInfoDto>
                data={state.data.items}
                columns={columns}
                keyField='batchId'
                loading={false}
                emptyMessage='No se encontraron lotes para este producto'
                pagination={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  total: state.data.totalItems || 0,
                  onPageChange: handlePageChange
                }}
                styling={{
                  dense: true,
                  hoverEffect: true
                }}
              />
            ) : (
              <Box className={classes.noDataMessage}>
                <Typography color='textSecondary'>
                  No se encontraron lotes para este producto
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {state.status === Status.Idle && (
          <Box className={classes.noDataMessage}>
            <Typography variant='caption' color='textSecondary'>
              Lotes detectados inicialmente: <strong>{batches.length}</strong>
            </Typography>
          </Box>
        )}
      </DialogContent>
    </EOSModal>
  )
}
